import { useEffect, useMemo, useRef } from "react";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Alert, AlertDescription } from "@/components/ui/alert";
import type { ComponentWithSource } from "../api/componentsData";
import {
  componentOptionLabel,
  getAssignableFields,
  getDefaultAssignFieldName,
} from "./inboxFieldUtils";
import {
  rememberInboxCollection,
  rememberInboxField,
  resolveInitialInboxSelection,
  resolveRememberedFieldName,
  loadInboxAssignPrefs,
} from "./inboxAssignPrefs";

const NO_COLLECTION = "__none__";

type CollectionFieldPickerProps = {
  collections: ComponentWithSource[];
  collectionSlug: string;
  fieldName: string;
  onCollectionChange: (slug: string, component: ComponentWithSource | null) => void;
  onFieldChange: (fieldName: string) => void;
  allowNone?: boolean;
  noneLabel?: string;
  collectionsLoading?: boolean;
  persistenceUserId?: string | null;
  restoreFromPrefs?: boolean;
};

export function CollectionFieldPicker({
  collections,
  collectionSlug,
  fieldName,
  onCollectionChange,
  onFieldChange,
  allowNone = false,
  noneLabel = "Inbox (assign later)",
  collectionsLoading = false,
  persistenceUserId,
  restoreFromPrefs = false,
}: CollectionFieldPickerProps) {
  const prefsAppliedRef = useRef(false);

  const selectedComponent = useMemo(() => {
    if (!collectionSlug || collectionSlug === NO_COLLECTION) return null;
    return collections.find((component) => component.slug === collectionSlug) ?? null;
  }, [collections, collectionSlug]);

  const assignableFields = useMemo(
    () => getAssignableFields(selectedComponent),
    [selectedComponent]
  );

  useEffect(() => {
    if (!restoreFromPrefs) {
      prefsAppliedRef.current = false;
    }
  }, [restoreFromPrefs]);

  useEffect(() => {
    if (!restoreFromPrefs || !persistenceUserId || collectionsLoading) return;
    if (prefsAppliedRef.current) return;

    prefsAppliedRef.current = true;

    const initial = resolveInitialInboxSelection(collections, persistenceUserId);
    if (!initial.component) return;

    onCollectionChange(initial.component.slug, initial.component);
    onFieldChange(initial.fieldName);
  }, [
    restoreFromPrefs,
    persistenceUserId,
    collections,
    collectionsLoading,
    onCollectionChange,
    onFieldChange,
  ]);

  useEffect(() => {
    if (!selectedComponent) return;
    if (fieldName && assignableFields.some((field: { name?: string }) => field.name === fieldName)) {
      return;
    }
    const prefs = loadInboxAssignPrefs(persistenceUserId);
    const nextField = persistenceUserId
      ? resolveRememberedFieldName(selectedComponent, prefs)
      : getDefaultAssignFieldName(selectedComponent);
    if (nextField) {
      onFieldChange(nextField);
    }
  }, [
    selectedComponent,
    assignableFields,
    fieldName,
    onFieldChange,
    persistenceUserId,
  ]);

  const showFieldPicker = Boolean(selectedComponent);
  const noAssignableFields = showFieldPicker && assignableFields.length === 0;

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label>Collection</Label>
        <Select
          value={collectionSlug || (allowNone ? NO_COLLECTION : "")}
          onValueChange={(value) => {
            if (value === NO_COLLECTION) {
              onCollectionChange("", null);
              onFieldChange("");
              return;
            }
            const component = collections.find((item) => item.slug === value) ?? null;
            onCollectionChange(value, component);
            if (component) {
              if (persistenceUserId) {
                rememberInboxCollection(persistenceUserId, component);
              }
              const prefs = loadInboxAssignPrefs(persistenceUserId);
              const nextField = persistenceUserId
                ? resolveRememberedFieldName(component, prefs)
                : getDefaultAssignFieldName(component);
              onFieldChange(nextField);
            } else {
              onFieldChange("");
            }
          }}
          disabled={collectionsLoading}
        >
          <SelectTrigger>
            <SelectValue placeholder={collectionsLoading ? "Loading..." : "Select collection"} />
          </SelectTrigger>
          <SelectContent>
            {allowNone ? (
              <SelectItem value={NO_COLLECTION}>{noneLabel}</SelectItem>
            ) : null}
            {collections.map((component) => (
              <SelectItem key={`${component.uid || component.slug}`} value={component.slug}>
                {componentOptionLabel(component)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {showFieldPicker ? (
        <div className="space-y-2">
          <Label>Field</Label>
          <Select
            value={fieldName}
            onValueChange={(value) => {
              onFieldChange(value);
              if (persistenceUserId && selectedComponent) {
                rememberInboxField(persistenceUserId, selectedComponent, value);
              }
            }}
            disabled={assignableFields.length === 0}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select field" />
            </SelectTrigger>
            <SelectContent>
              {assignableFields.map((field: { name: string; label?: string }) => (
                <SelectItem key={field.name} value={field.name}>
                  {field.label || field.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      ) : null}

      {noAssignableFields ? (
        <Alert variant="destructive">
          <AlertDescription>
            This collection has no text field to assign to.
          </AlertDescription>
        </Alert>
      ) : null}
    </div>
  );
}

export { NO_COLLECTION };
